/* eslint-disable react/no-array-index-key */
/* eslint-disable react-hooks/exhaustive-deps */
import { useMediaQuery } from '@material-ui/core';
import { Theme, withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import { useLanguage } from '@orientaction/hooks';
import { BarController, BarElement, Chart as ChartJS, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { fontFamily } from 'html2canvas/dist/types/css/property-descriptors/font-family';
import { FC, useEffect, useMemo, useState } from 'react';
// import { Bar } from 'react-chartjs-2';

ChartJS.register(BarElement, ChartDataLabels, Legend, BarController);

interface IProps {
  data: any;
  typeTest?: any;
}

const Diagram: FC<IProps> = ({ data: propsData, typeTest }) => {
  const { language, translateWord } = useLanguage();
  const [graphLabels, setGraphLabels] = useState<Array<string>>([]);
  const [openT, setOpenT] = useState<any>(null);

  const LightTooltipChart = withStyles((theme: Theme) => ({
    tooltip: {
      backgroundColor: '#000A8CE5',
      color: '#FAFAFA',
      boxShadow: theme.shadows[1],
      fontSize: 11,
      fontFamily: 'Poppins',
      fontWeight: 700,
    },
    popper: {
      marginTop: -20,
    }
  }))(Tooltip);

  useEffect(() => {
    (async () => {
      const dataLabels =
        propsData?.length > 6
          ? propsData.map(async (obj: any) => (await translateWord(obj.label))[0])
          : propsData
              .concat(Array.from({ length: 6 - propsData.length }, () => ({ label: '', value: 0 })))
              .map(async (obj: any) => (await translateWord(obj.label))[0]);

      setGraphLabels(await Promise.all(dataLabels));
    })();
  }, [propsData, language]);

  const concatenedDataValues = useMemo(() => {
    return propsData
      .concat(Array.from({ length: 6 - propsData.length }, () => ({ label: '', value: 0 })))
      .map((obj: any) => obj.value);
  }, [propsData]);

  // Styles regroupant couleurs et bordures
  const styles = [
    { color: '#FDDA5D', border: '#F3BD02' },
    { color: '#F9BC5D', border: '#E79009' },
    { color: '#F18965', border: '#DC4714' },
    { color: '#D45E6B', border: '#A12B38' },
    { color: '#BB3570', border: '#932A58' },
    { color: '#8B2D90', border: '#662169' },
    { color: '#602faa', border: '#602faa' },
    { color: '#414197', border: '#414197' },
  ];

  const concatenedDataColor = useMemo(() => {
    return propsData
      .concat(Array.from({ length: 6 - propsData.length }, () => ({ label: '', value: 0 })))
      .map((obj: any, index: number) => {
        if (obj.codeColor) {
          return obj.codeColor;
        }
        return styles[index % styles.length].color;
      });
  }, [propsData]);

  const data = useMemo(() => {
    return {
      labels: graphLabels?.filter((label: any) => label !== ''),
      datasets: [
        {
          order: 1,
          data: concatenedDataValues,
          barThickness: graphLabels?.length > 8 ? 15 : 19,
          backgroundColor: typeTest === 5 ? concatenedDataColor : styles.map(style => style.color),
        },
        {
          order: 1,
          data: [],
          backgroundColor: [
            '#FDCE29',
            '#F7A82E',
            '#ED6637',
            '#C93646',
            '#942A59',
            '#662169',
            '#3E1877',
            '#0E0E87',
          ],
        },
      ],
    };
  }, [graphLabels, concatenedDataValues]);

  // useEffect(() => {
  // }, [data, graphLabels, concatenedDataValues]);

  const getAnchor = (label: any) => {
    return concatenedDataValues[label.dataIndex] > 0 ? 'start' : 'end';
  };

  const getColor = (label: any) => {
    if (graphLabels[label.dataIndex]?.trim()?.length === 0) {
      return '#fff';
    }
    return concatenedDataValues[label.dataIndex] > 0 ? '#fff' : '#0E0E87';
  };

  const options = {
    indexAxis: 'y',
    maintainAspectRatio: false,
    responsive: true,
    // maxBarThickness: 55,
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        display: true,
        color: getColor,
        align: 'right',
        anchor: getAnchor,
        font: {
          size: 12,
          weight: 'bold',
          family: 'ITC Avant Garde Gothic Std Medium',
        },
        formatter(value: any) {
          return `${value} ${typeTest === 5 ? '' : '%'}`;
        },
      },
    },

    scales: {
      x: {
        max: typeTest === 5 ? concatenedDataValues[0] : 100,
        min: 0,
        position: 'bottom',
        display: false,
        grid: {
          display: true,
        },
        ticks: {
          display: false,
        },
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#0E1247',
          font: { size: 12, family: 'ITC Avant Garde Gothic Std Medium' },
          fontWeight: 500,
          mirror: true,
          z: 1,
          labelOffset: data?.labels?.length >= 9 ? -23 : data?.labels?.length === 8 ? -28 : -35,
        },
      },
    },
  };
  // Trouver la valeur max pour normaliser la largeur des barres
  const maxValue = Math.max(...propsData.map((item: any) => item.value)) || 1;

  const handleClick = (index: number) => {
    setOpenT((prev: any) => (prev === index ? null : index));
  };

  const handleClose = () => {
    setOpenT(null);
  };
  const isXS = useMediaQuery((theme: Theme) => theme.breakpoints.down('xs'));
  return (
    <div style={{ width: '100%', padding: 0 }}>
      {propsData.map((item: any, idx: number) => (
        <div key={idx} style={{ paddingBottom: 12 }}>
          {/* Label au-dessus de la barre */}
          <div
            style={{
              fontWeight: 400,
              color: '#0E1247',
              fontSize: 12,
              textAlign: 'left',
              fontFamily: 'Poppins',
              whiteSpace: 'normal',
              wordWrap: 'break-word',
            }}>
            {item.label}
          </div>
          {/* Ligne fond arrondi + barre + valeur */}
          {!isXS && (
            <div
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                overflow: 'visible',
              }}>
              {/* Barre colorée */}
              <LightTooltipChart title={item.textRestitutionChart || ''}>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 12,
                    textAlign: 'center',
                    verticalAlign: 'middle',
                    color: item?.value === 0 ? '#0E1247' : typeTest === 5 ? '#FAFAFA' : idx < 2 ? '#000000' : '#FAFAFA',
                    height: 24,
                    background: item?.color ? item?.color : styles[idx % styles.length].color,
                    borderTopRightRadius: 24,
                    borderBottomRightRadius: 24,
                    border:
                      item?.value !== 0
                        ? `1px solid ${
                            item?.color ? item?.color : styles[idx % styles.length].border
                          }`
                        : 'none',
                    marginLeft: 0,
                    paddingLeft: item.value !== 0 ? 8 : 0,
                    width: `${(item.value / maxValue) * 100}%`,
                    maxWidth: '100%',
                    transition: 'width 0.5s',
                    display: 'flex',
                    alignItems: 'center',
                    boxShadow: item?.value !== 0 ? '0 2px 8px rgba(0,0,0,0.03)' : 'none',
                    position: 'relative',
                    cursor: 'pointer',
                  }}>
                  <span
                    style={{
                      fontWeight: 700,
                      fontSize: 12,
                      textAlign: 'center',
                      verticalAlign: 'middle',
                      color: item?.value === 0 ? '#0E1247' : typeTest === 5 ? '#FAFAFA' : idx < 2 ? '#000000' : '#FAFAFA',
                      display: 'flex',
                      alignItems: 'center',
                      height: '100%',
                      marginBottom: '-0.1rem',
                      fontFamily: 'Poppins',
                      textWrap: 'nowrap',
                    }}>
                    {`${item.value} ${typeTest === 5 ? '' : '%'}`}
                  </span>
                </div>
              </LightTooltipChart>
            </div>
          )}
          {isXS && (
            <div
              onClick={() => handleClick(idx)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                overflow: 'visible',
              }}>
              {/* Barre colorée */}
              <LightTooltipChart
                title={item.textRestitutionChart || ''}
                open={openT === idx}
                disableFocusListener={true}
                disableHoverListener={true}
                disableTouchListener={true}
                placement="bottom-start"
                onClose={handleClose}>
                <div
                  style={{
                    height: 24,
                    background: item?.color ? item?.color : styles[idx % styles.length].color,
                    borderTopRightRadius: 24,
                    borderBottomRightRadius: 24,
                    border:
                      item?.value !== 0
                        ? `1px solid ${
                            item?.color ? item?.color : styles[idx % styles.length].border
                          }`
                        : 'none',
                    marginLeft: 0,
                    paddingLeft: item.value !== 0 ? 8 : 0,
                    width: `${(item.value / maxValue) * 100}%`,
                    maxWidth: '100%',
                    transition: 'width 0.5s',
                    display: 'flex',
                    alignItems: 'center',
                    boxShadow: item?.value !== 0 ? '0 2px 8px rgba(0,0,0,0.03)' : 'none',
                    position: 'relative',
                    cursor: 'pointer',
                  }}>
                  <span
                    style={{
                      fontWeight: 700,
                      fontSize: 12,
                      textAlign: 'center',
                      verticalAlign: 'middle',
                      color: item?.value === 0 ? '#0E1247' : typeTest === 5 ? '#FAFAFA' : idx < 2 ? '#000000' : '#FAFAFA',
                      display: 'flex',
                      alignItems: 'center',
                      height: '100%',
                      marginBottom: '-0.1rem',
                      fontFamily: 'Poppins',
                      textWrap: 'nowrap',
                    }}>
                    {`${item.value} ${typeTest === 5 ? '' : '%'}`}
                  </span>
                </div>
              </LightTooltipChart>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default Diagram;
